/**
 * A split room's ceiling, CUT where the new wall stands.
 *
 * The ceiling a scan's processing built — a raised centre with soffits round
 * it, a double-height plane, a slope — rests on evidence one room split does
 * not have, and rebuilding each piece from its walls alone (what the split did
 * until 2026-10-23) settled most such ceilings on the lowest wall and pulled
 * the taller walls down to it: 142 of 189 corpus splits in rooms with an
 * uneven ceiling (reference-docs/room-split/overview.md). A wall changes the
 * ceiling only where it stands, so each piece keeps the part of every facet on
 * its side, at the heights it had.
 *
 * Where the wall stands is its strip in the room. The generator runs a
 * ceiling on past the floor's outline, though (in 40 of 340 corpus rooms, by
 * up to 2 m), and there a facet can wrap round the wall's end and join the
 * two sides; such a part is split along the wall's own line, which takes
 * nothing out — past the room there is no wall — and only tells the sides
 * apart. A part that joins nothing is never split, so a U-room's far arm
 * keeps its ceiling whole.
 *
 * Each part goes to the piece it lies over; a part over neither (ceiling past
 * the outline) to the nearer. A flat or sloped facet is cut in plan and lifted
 * back onto its own plane; an upright one (a soffit's drop, an endcap, a
 * bridge) is trimmed along its own line. A facet the cut misses comes through
 * unchanged. A soffit's `baseWallId`, and a facet's `wallIds`, follow their
 * wall onto the piece of it on the facet's side.
 *
 * Pure. X-Z plan, Y up.
 */

const polygonClipping = require('polygon-clipping');
const {
  signedAreaXZ,
  splitPolygonByLine,
} = require('../geometry/polygonSplit');
const {
  isPointInsidePolygon,
  isPointInsideOrOnPolygon,
} = require('../geometry/pointInPolygon');
const { planeOf, isUpright, yOnPlane } = require('../geometry/facetPlane');
const { distanceToPolygonBoundary, wallEndpoints } = require('./geometry');
const { planOverlapArea } = require('./overlap');
const { clipFloorTexture } = require('./regions-remap');
const { withSnapRetry } = require('../geometry/clipRetry');

const DUST_M2 = 1e-6; // a region smaller than this is clipper dust
const SAME_POINT_M = 1e-7; // a vertex this close to one of the facet's is it
const ON_PIECE_M = 1e-3; // a point this close to a piece's outline is on it
const SAME_LINE_M = 0.02; // a wall this close to another's line is it, cut
// A facet's thickness when it carries none: the generator's own
// (`calculateDimensionsFromPolygon(oriented, 0.1)` in ceiling-generation).
const DEFAULT_THICKNESS_M = 0.1;

const toPair = (p) => [p.x, p.z];
const ringOf = (pts) => pts.map(toPair);
const fromRing = (r) => {
  const pts = r.map(([x, z]) => ({ x, z }));
  const a = pts[0];
  const b = pts[pts.length - 1];
  return pts.length > 1 && a.x === b.x && a.z === b.z ? pts.slice(0, -1) : pts;
};
const regionArea = (poly) =>
  poly.reduce((s, r, k) => {
    const a = Math.abs(signedAreaXZ(fromRing(r)));
    return s + (k === 0 ? a : -a);
  }, 0);
const inRegions = (p, mp) =>
  mp.some(
    (poly) =>
      isPointInsidePolygon(p, fromRing(poly[0])) &&
      !poly.slice(1).some((h) => isPointInsidePolygon(p, fromRing(h)))
  );

/** The piece a part of the ceiling lies over, else the nearer one. */
function pieceFor(ringXZ, pieces) {
  const over = pieces.map((p) => planOverlapArea(ringXZ, p.polygon));
  const best = over.indexOf(Math.max(...over));
  if (over[best] > DUST_M2) return best;
  return nearestPiece(ringXZ, pieces);
}

function nearestPiece(points, pieces) {
  let best = 0;
  let bestD = Infinity;
  pieces.forEach((piece, i) => {
    for (const p of points) {
      const d = distanceToPolygonBoundary(p, piece.polygon);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
  });
  return best;
}

/** The facet's own vertex at this spot (within `tol`), else null. */
function originalAt(corners, x, z, tol) {
  return (
    corners.find((c) => Math.abs(c.x - x) <= tol && Math.abs(c.z - z) <= tol) ||
    null
  );
}

const overlapsOf = (ringXZ, pieces) =>
  pieces.map((p) => planOverlapArea(ringXZ, p.polygon));
const overBoth = (over) => over.filter((a) => a > DUST_M2).length > 1;

/** What lies over each piece, for a part the cut could not tell apart. */
function intersectEach(poly, pieces) {
  return pieces.flatMap((p, i) =>
    polygonClipping
      .intersection([poly], [[ringOf(p.polygon)]])
      .filter((q) => regionArea(q) > DUST_M2)
      .map((q) => ({ piece: i, ring: fromRing(q[0]) }))
  );
}

/**
 * A part of a facet left joined round the wall's end — past the outline, where
 * the generator ran the ceiling on — split along each end's line, then cleared
 * of the strip again: `[{ piece, ring }]`.
 */
function splitRoundEnds(poly, cut, pieces) {
  let rings = [fromRing(poly[0])];
  for (const { point, dir } of cut.ends) {
    const n = { x: -dir.z, z: dir.x };
    const c = point.x * n.x + point.z * n.z;
    rings = rings.flatMap((r) => {
      if (!overBoth(overlapsOf(r, pieces))) return [r];
      const { front, back } = splitPolygonByLine(r, n, c);
      return front.length + back.length ? [...front, ...back] : [r];
    });
  }
  const out = [];
  for (const r of rings) {
    const parts = polygonClipping
      .difference([ringOf(r)], cut.wallMp)
      .filter((q) => regionArea(q) > DUST_M2);
    for (const q of parts) {
      const ring = fromRing(q[0]);
      const over = overlapsOf(ring, pieces);
      if (overBoth(over)) out.push(...intersectEach(q, pieces));
      else out.push({ piece: pieceFor(ring, pieces), ring });
    }
  }
  return out;
}

/** Thrown when the clipper cannot cut a facet the wall stands across. */
class UncuttableCeiling extends Error {}

// A stretch of outline that leaves a point and comes back within 2 cm of it,
// enclosing under 2 cm of width on average (area < this x perimeter), is a
// sliver: a spike out and back, never ceiling anyone sees.
const SLIVER_GAP_M = 0.02;
const SLIVER_WIDTH_M = 0.01;

const perimeterOf = (r) =>
  r.reduce((sum, p, i) => {
    const q = r[(i + 1) % r.length];
    return sum + Math.hypot(q.x - p.x, q.z - p.z);
  }, 0);
const isSliver = (r) =>
  r.length < 3 || Math.abs(signedAreaXZ(r)) < SLIVER_WIDTH_M * perimeterOf(r);
const samePoint = (p, q) =>
  Math.abs(p.x - q.x) < SAME_POINT_M && Math.abs(p.z - q.z) < SAME_POINT_M;
const dedupe = (pts) =>
  pts.filter((p, i) => !samePoint(p, pts[(i + pts.length - 1) % pts.length]));

/**
 * The outline without what the clipper cannot take: a repeated corner, and a
 * sliver the outline runs out and back along. A corpus great room's main
 * facet carries a 2 mm spike 6 m into the room, and cut as stored the clipper
 * throws on it.
 */
function withoutSlivers(ring) {
  let pts = dedupe(ring);
  for (let changed = true; changed; ) {
    changed = false;
    for (let i = 0; i < pts.length && !changed; i++) {
      // j runs to the corner before i's neighbour round the closing edge.
      for (let j = i + 2; j < pts.length - (i === 0 ? 1 : 0) && !changed; j++) {
        const gap = Math.hypot(pts[j].x - pts[i].x, pts[j].z - pts[i].z);
        if (gap > SLIVER_GAP_M) continue;
        const loop = pts.slice(i, j + 1);
        const rest = [...pts.slice(0, i + 1), ...pts.slice(j)];
        const next = isSliver(loop)
          ? dedupe(rest)
          : isSliver(rest)
            ? dedupe(loop)
            : null;
        if (next && next.length >= 3 && next.length < pts.length) {
          pts = next;
          changed = true;
        }
      }
    }
  }
  return pts;
}

/** The facet's plan as the clipper resolves it, slivers gone. */
function cleanPlan(ring) {
  return polygonClipping
    .union([ringOf(withoutSlivers(ring))])
    .filter((poly) => regionArea(poly) > DUST_M2);
}

const WHOLE = Symbol('whole');

/**
 * The facet's plan cut where the wall stands: WHOLE when the wall misses it,
 * else `[{ piece, ring }]`. Throws when the clipper refuses the input.
 */
function cutPlan(ring, cut, pieces) {
  const plan = cleanPlan(ring);
  const regions = polygonClipping
    .difference(plan, cut.wallMp)
    .filter((poly) => regionArea(poly) > DUST_M2);
  const planArea = plan.reduce((sum, poly) => sum + regionArea(poly), 0);
  const cutArea = regions.reduce((sum, poly) => sum + regionArea(poly), 0);
  if (Math.abs(cutArea - planArea) < DUST_M2) return WHOLE;
  return regions.flatMap((poly) =>
    overBoth(overlapsOf(fromRing(poly[0]), pieces))
      ? splitRoundEnds(poly, cut, pieces)
      : [
          {
            piece: pieceFor(fromRing(poly[0]), pieces),
            ring: fromRing(poly[0]),
          },
        ]
  );
}

/** The cut and the pieces with every coordinate on the snap grid. */
function snapped(cut, pieces, snap) {
  const s = (p) => ({ x: snap(p.x), z: snap(p.z) });
  return {
    cut: {
      ...cut,
      wallMp: cut.wallMp.map((poly) =>
        poly.map((r) => r.map(([x, z]) => [snap(x), snap(z)]))
      ),
      ends: cut.ends.map((e) => ({ ...e, point: s(e.point) })),
    },
    pieces: pieces.map((p) => ({ ...p, polygon: p.polygon.map(s) })),
    s,
  };
}

/** A flat or sloped facet, cut in plan: `[{ piece, corners, ringXZ }]`. */
function flatParts(facet, plane, cut, pieces) {
  const corners = facet.polygonCorners;
  const ring = corners.map(({ x, z }) => ({ x, z }));
  const run = withSnapRetry(
    (snap) => {
      const on = snapped(cut, pieces, snap);
      return cutPlan(ring.map(on.s), on.cut, on.pieces);
    },
    { exactFirst: true }
  );
  if (!run) throw new UncuttableCeiling(facet.id);
  if (run.value === WHOLE)
    return [{ piece: pieceFor(ring, pieces), whole: true }];
  // On a grid, a vertex the cut kept moved by up to a cell: it is still the
  // facet's own, with its own height.
  const tol = run.grid ? 1 / run.grid : SAME_POINT_M;
  const winding = Math.sign(signedAreaXZ(ring));
  return run.value.map(({ piece, ring: r }) => {
    const outer = Math.sign(signedAreaXZ(r)) === winding ? r : [...r].reverse();
    return {
      piece,
      ringXZ: outer,
      corners: outer.map(
        ({ x, z }) =>
          originalAt(corners, x, z, tol) || { x, y: yOnPlane(plane, x, z), z }
      ),
    };
  });
}

/** Sutherland-Hodgman: the (s, y) polygon with s in [s0, s1]. */
function clipToSpan(pts, s0, s1) {
  const clip = (input, keep, at) => {
    const out = [];
    for (let i = 0; i < input.length; i++) {
      const p = input[i];
      const q = input[(i + 1) % input.length];
      if (keep(p)) out.push(p);
      if (keep(p) !== keep(q)) {
        const t = (at - p.s) / (q.s - p.s);
        out.push({ s: at, y: p.y + t * (q.y - p.y) });
      }
    }
    return out;
  };
  return clip(
    clip(pts, (p) => p.s >= s0, s0),
    (p) => p.s <= s1,
    s1
  );
}

/** Where on a → b (as metres from a) the region's edges cross it. */
function crossingsOn(a, b, mp) {
  const d = { x: b.x - a.x, z: b.z - a.z };
  const len = Math.hypot(d.x, d.z);
  const out = [];
  for (const poly of mp) {
    for (const r of poly) {
      const pts = fromRing(r);
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        const q = pts[(i + 1) % pts.length];
        const e = { x: q.x - p.x, z: q.z - p.z };
        const den = d.x * e.z - d.z * e.x;
        if (Math.abs(den) < 1e-12) continue;
        const ap = { x: p.x - a.x, z: p.z - a.z };
        const t = (ap.x * e.z - ap.z * e.x) / den;
        const w = (ap.x * d.z - ap.z * d.x) / den;
        if (t > 0 && t < 1 && w >= 0 && w <= 1) out.push(t * len);
      }
    }
  }
  return out;
}

/** An upright facet, trimmed along its own line: `[{ piece, corners }]`. */
function uprightParts(facet, cut, pieces) {
  const corners = facet.polygonCorners;
  let i0 = 0;
  let i1 = 0;
  let far = 0;
  for (let i = 0; i < corners.length; i++) {
    for (let j = i + 1; j < corners.length; j++) {
      const d = Math.hypot(
        corners[j].x - corners[i].x,
        corners[j].z - corners[i].z
      );
      if (d > far) {
        far = d;
        i0 = i;
        i1 = j;
      }
    }
  }
  const o = corners[i0];
  if (!(far > SAME_POINT_M)) {
    return [{ piece: nearestPiece([o], pieces), whole: true }];
  }
  const dir = {
    x: (corners[i1].x - o.x) / far,
    z: (corners[i1].z - o.z) / far,
  };
  const at = (s) => ({ x: o.x + s * dir.x, z: o.z + s * dir.z });
  const pts = corners.map((p) => ({
    s: (p.x - o.x) * dir.x + (p.z - o.z) * dir.z,
    y: p.y,
    p,
  }));
  const sMin = Math.min(...pts.map((p) => p.s));
  const sMax = Math.max(...pts.map((p) => p.s));
  // Where the facet's line crosses the strip, or a line on from the wall's
  // end out of the room (a ceiling run on past the outline).
  const rays = cut.ends.map(({ point, dir }) => [
    [
      [point.x, point.z],
      [point.x + dir.x * cut.reach, point.z + dir.z * cut.reach],
    ],
  ]);
  const breaks = [
    sMin,
    sMax,
    ...crossingsOn(at(sMin), at(sMax), [...cut.wallMp, ...rays]).map(
      (s) => sMin + s
    ),
  ]
    .sort((p, q) => p - q)
    .filter((s, k, all) => k === 0 || s - all[k - 1] > SAME_POINT_M);

  const spans = [];
  for (let k = 0; k + 1 < breaks.length; k++) {
    const mid = at((breaks[k] + breaks[k + 1]) / 2);
    if (inRegions(mid, cut.wallMp)) continue;
    const inside = pieces.findIndex((p) =>
      isPointInsideOrOnPolygon(mid, p.polygon, ON_PIECE_M)
    );
    const piece = inside >= 0 ? inside : nearestPiece([mid], pieces);
    const last = spans[spans.length - 1];
    if (
      last &&
      last.piece === piece &&
      Math.abs(last.s1 - breaks[k]) < SAME_POINT_M
    ) {
      last.s1 = breaks[k + 1];
    } else {
      spans.push({ piece, s0: breaks[k], s1: breaks[k + 1] });
    }
  }
  if (
    spans.length === 1 &&
    spans[0].s0 - sMin < SAME_POINT_M &&
    sMax - spans[0].s1 < SAME_POINT_M
  ) {
    return [{ piece: spans[0].piece, whole: true }];
  }
  return spans.map(({ piece, s0, s1 }) => ({
    piece,
    corners: clipToSpan(pts, s0, s1).map((q) =>
      q.p ? q.p : { ...at(q.s), y: q.y }
    ),
  }));
}

/** Bounding-box centre and extent, the generator's convention for a facet. */
function boxOf(corners, thickness) {
  const ext = (k) => [
    Math.min(...corners.map((p) => p[k])),
    Math.max(...corners.map((p) => p[k])),
  ];
  const [x0, x1] = ext('x');
  const [y0, y1] = ext('y');
  const [z0, z1] = ext('z');
  return {
    center: { x: (x0 + x1) / 2, y: (y0 + y1) / 2, z: (z0 + z1) / 2 },
    dimensions: {
      x: Math.max(x1 - x0, 0.1),
      y: Math.max(z1 - z0, 0.1),
      z: thickness,
    },
  };
}

/** Is wall `w` the same wall as `src`, or a cut piece of it? */
function sameLine(src, w) {
  const a = wallEndpoints(src);
  const b = wallEndpoints(w);
  if (!a || !b) return false;
  const da = { x: a.br.x - a.bl.x, z: a.br.z - a.bl.z };
  const db = { x: b.br.x - b.bl.x, z: b.br.z - b.bl.z };
  const la = Math.hypot(da.x, da.z);
  const lb = Math.hypot(db.x, db.z);
  if (!(la > 0) || !(lb > 0)) return false;
  if (Math.abs(da.x * db.z - da.z * db.x) / (la * lb) > 0.01) return false;
  const off =
    Math.abs((b.bl.x - a.bl.x) * da.z - (b.bl.z - a.bl.z) * da.x) / la;
  return off < SAME_LINE_M;
}

/** The facet's wall references, carried onto the piece's own walls. */
function retieWalls(facet, pieceWalls, sourceWalls) {
  if (!pieceWalls) return facet;
  const ids = new Set(pieceWalls.map((w) => w?.id));
  const onPiece = (id) => {
    if (ids.has(id)) return id;
    const src = (sourceWalls || []).find((w) => w?.id === id);
    return (src && pieceWalls.find((w) => sameLine(src, w))?.id) || null;
  };
  let next = facet;
  if (facet.baseWallId != null) {
    const id = onPiece(facet.baseWallId);
    if (id !== facet.baseWallId) {
      next = { ...next };
      if (id) next.baseWallId = id;
      else delete next.baseWallId;
    }
  }
  if (Array.isArray(facet.wallIds)) {
    const wallIds = [...new Set(facet.wallIds.map(onPiece).filter(Boolean))];
    if (wallIds.join() !== facet.wallIds.join()) next = { ...next, wallIds };
  }
  return next;
}

/**
 * The room's ceiling, cut where the new wall stands.
 *
 * @param {object[]} ceilings the room's `ceilings`
 * @param {object} cut
 * @param {{x,z}[]} cut.room the room's floor outline
 * @param {{x,z}[]} [cut.wall] the wall's strip in the room (a caller may pass
 *   it in `cutAway` instead)
 * @param {{ point: {x,z}, dir: {x,z} }[]} cut.ends where the wall meets the
 *   outline, and the unit direction it was heading out of the room
 * @param {{ polygon: {x,z}[], renameId?: (id, k) => string, walls?: object[] }[]} cut.pieces
 *   each piece's outline; how to name its facets (`k` counts the parts one
 *   facet leaves on the piece; by default the first keeps the id and the
 *   rest are `<id>-part<k>`); and its room walls, for the wall references
 * @param {object[]} [cut.sourceWalls] the room's walls before the split
 * @param {{x,z}[][]} [cut.cutAway] more of the plan to take out with the wall
 *   (a gap close's strip of guessed outline)
 * @returns {object[][]|null} the facets for each piece, in `pieces` order;
 *   null when the clipper cannot cut a facet the wall stands across, so the
 *   caller can build the pieces' ceilings some other way rather than hand one
 *   piece the other's
 */
function splitCeilings(
  ceilings,
  { room, wall, ends, pieces, sourceWalls, cutAway = [] }
) {
  const all = (ceilings || []).flatMap((c) => c?.polygonCorners || []);
  const pts = [...all, ...room];
  const span = (k) =>
    Math.max(...pts.map((p) => p[k])) - Math.min(...pts.map((p) => p[k]));
  const cut = {
    wallMp: [
      ...(wall?.length >= 3 ? [[ringOf(wall)]] : []),
      ...cutAway.map((r) => [ringOf(r)]),
    ],
    ends: ends || [],
    // Far enough that a line on from an end crosses every facet.
    reach: Math.hypot(span('x'), span('z')) + 1,
  };

  const out = pieces.map(() => []);
  const count = new Map();
  for (const facet of ceilings || []) {
    const corners = facet?.polygonCorners;
    const plane = planeOf(corners);
    if (!plane) {
      out[0].push(facet);
      continue;
    }
    let parts;
    try {
      parts = isUpright(plane)
        ? uprightParts(facet, cut, pieces)
        : flatParts(facet, plane, cut, pieces);
    } catch (err) {
      if (err instanceof UncuttableCeiling) return null;
      throw err;
    }
    for (const part of parts) {
      const piece = pieces[part.piece];
      const key = `${part.piece}\u0000${facet.id}`;
      const k = count.get(key) || 0;
      count.set(key, k + 1);
      const rename = piece.renameId || ((id, n) => (n ? `${id}-part${n}` : id));
      const id = facet.id == null ? facet.id : rename(facet.id, k);
      let next;
      if (part.whole) {
        next = id === facet.id ? facet : { ...facet, id };
      } else {
        const ring = corners.map(({ x, z }) => ({ x, z }));
        next = {
          ...facet,
          id,
          polygonCorners: part.corners,
          ...boxOf(part.corners, facet.dimensions?.z ?? DEFAULT_THICKNESS_M),
          ...(facet.texture !== undefined
            ? {
                texture:
                  part.ringXZ && facet.texture
                    ? clipFloorTexture(facet.texture, ring, part.ringXZ)
                    : facet.texture,
              }
            : {}),
        };
      }
      out[part.piece].push(retieWalls(next, piece.walls, sourceWalls));
    }
  }
  return out;
}

/** A facet with height over the floor: what a room needs to have a ceiling. */
const isOverhead = (c) => {
  const plane = planeOf(c?.polygonCorners);
  return !!plane && !isUpright(plane);
};

/**
 * A room's ceiling after a gap close moved part of its outline onto a wall's
 * face (`functions/features/room-gap.js`): CUT where the wall stands and over
 * the strip of guessed outline the room gave up, never rebuilt. A close nearly
 * always takes area away (23 of 24 corpus closes); one that ADDS floor has no
 * ceiling ever measured over it, so that one is left to be built. A sliver is
 * not floor (`isSliver`): re-seating a flank corner adds a few mm² of one.
 *
 * @param {object[]} ceilings the room's `ceilings`
 * @param {object} close
 * @param {{x,z}[]} close.room the room's outline before the close
 * @param {{x,z}[]} close.outline its outline after
 * @param {{x,z}[]} close.wall the closing wall's footprint
 * @param {{ point, dir }[]} close.ends the wall's ends, and the way out of each
 * @param {object[]} [close.sourceWalls] the room's walls before
 * @param {object[]} [close.walls] its walls after
 * @returns {object[]|null} the room's facets; null when the close added area,
 *   or the clipper could not cut the ceiling
 */
function trimCeilingsToOutline(
  ceilings,
  { room, outline, wall, ends, sourceWalls, walls }
) {
  const run = withSnapRetry(
    (snap) => {
      const mp = (poly) => [[poly.map((p) => [snap(p.x), snap(p.z)])]];
      return {
        lost: polygonClipping.difference(mp(room), mp(outline)),
        added: polygonClipping.difference(mp(outline), mp(room)),
        // Never the ceiling over the room's new floor: only what of the
        // wall's footprint lies outside it (near a corner the footprint runs
        // on into the room).
        wallOut: polygonClipping.difference(mp(wall), mp(outline)),
      };
    },
    { exactFirst: true }
  );
  if (!run) return null;
  const lost = run.value.lost
    .filter((poly) => regionArea(poly) > DUST_M2)
    .map((poly) => fromRing(poly[0]));
  const wallOut = run.value.wallOut
    .filter((poly) => regionArea(poly) > DUST_M2)
    .map((poly) => fromRing(poly[0]));
  // The strip given up goes with the wall: cut away, and a piece of its own,
  // so ceiling run on past it (beyond the old guessed edge) goes with it too.
  const out = splitCeilings(ceilings, {
    room,
    ends,
    sourceWalls,
    cutAway: [...wallOut, ...lost],
    pieces: [
      { polygon: outline, walls },
      ...lost.map((polygon) => ({ polygon })),
    ],
  });
  if (!out) return null;
  // A sliver is not floor: re-seating a flank corner adds a few mm² of one.
  const added = run.value.added
    .filter((poly) => regionArea(poly) > DUST_M2)
    .map((poly) => fromRing(poly[0]))
    .filter((r) => !isSliver(r));
  let kept = out[0];
  for (const r of added) {
    kept = runOnOver(kept, r);
    if (!kept) return null;
  }
  return kept;
}

/** How much of ring `r`'s outline lies along `ring`'s. */
function sharedLength(r, ring) {
  let len = 0;
  for (let i = 0; i < r.length; i++) {
    const p = r[i];
    const q = r[(i + 1) % r.length];
    const mid = { x: (p.x + q.x) / 2, z: (p.z + q.z) / 2 };
    if (distanceToPolygonBoundary(mid, ring) < ON_PIECE_M) {
      len += Math.hypot(q.x - p.x, q.z - p.z);
    }
  }
  return len;
}

/**
 * The facets with new floor `r` under a ceiling: the flat or sloped facet
 * that borders it most is run on over it, on its own plane — the ceiling the
 * room had, carried to where the room now reaches. Null when no facet borders
 * it (there is no ceiling to carry), or the clipper cannot join them.
 */
function runOnOver(facets, r) {
  let best = -1;
  let bestLen = 0;
  facets.forEach((c, i) => {
    if (!isOverhead(c)) return;
    const len = sharedLength(
      r,
      c.polygonCorners.map(({ x, z }) => ({ x, z }))
    );
    if (len > bestLen) {
      bestLen = len;
      best = i;
    }
  });
  if (best < 0) return null;
  const facet = facets[best];
  const corners = facet.polygonCorners;
  const ring = corners.map(({ x, z }) => ({ x, z }));
  const joined = withSnapRetry(
    (snap) => {
      const mp = (poly) => [[poly.map((p) => [snap(p.x), snap(p.z)])]];
      return polygonClipping.union(mp(ring), mp(r));
    },
    { exactFirst: true }
  );
  if (!joined || joined.value.length !== 1) return null;
  const tol = joined.grid ? 1 / joined.grid : SAME_POINT_M;
  const plane = planeOf(corners);
  let outer = fromRing(joined.value[0][0]);
  if (Math.sign(signedAreaXZ(outer)) !== Math.sign(signedAreaXZ(ring))) {
    outer = outer.reverse();
  }
  const next = outer.map(
    ({ x, z }) =>
      originalAt(corners, x, z, tol) || { x, y: yOnPlane(plane, x, z), z }
  );
  const grown = {
    ...facet,
    polygonCorners: next,
    ...boxOf(next, facet.dimensions?.z ?? DEFAULT_THICKNESS_M),
    ...(facet.texture
      ? { texture: clipFloorTexture(facet.texture, ring, outer) }
      : {}),
  };
  return facets.map((c, i) => (i === best ? grown : c));
}

module.exports = { splitCeilings, trimCeilingsToOutline, isOverhead };
